import { useEffect, useState } from 'react';
import { orgAPI } from '../api/orgAPI.js';
import { isBillingInactive } from './billingPaymentRequired.js';

const BILLING_STATUS_INVALIDATE_EVENT = 'quazbit:billing-status-invalidate';

let cachedStatus = undefined;
let cachedAt = 0;
let inflight = null;

const CACHE_MS = 30_000;

async function fetchBillingStatus({ force = false } = {}) {
    const now = Date.now();
    if (!force && cachedStatus !== undefined && now - cachedAt < CACHE_MS) {
        return cachedStatus;
    }
    if (!force && inflight) return inflight;

    inflight = orgAPI
        .getBilling()
        .then(({ data }) => {
            cachedStatus = data?.billing?.status ?? null;
            cachedAt = Date.now();
            return cachedStatus;
        })
        .catch(() => {
            cachedStatus = null;
            cachedAt = Date.now();
            return cachedStatus;
        })
        .finally(() => {
            inflight = null;
        });

    return inflight;
}

/** Clear cache after checkout / plan change so the gate rechecks promptly. */
export function invalidateBillingStatusCache() {
    cachedStatus = undefined;
    cachedAt = 0;
    inflight = null;
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(BILLING_STATUS_INVALIDATE_EVENT));
    }
}

/**
 * Shared billing status for route gates (inactive trial/subscription lock).
 */
export function useBillingStatus() {
    const [status, setStatus] = useState(cachedStatus);
    const [ready, setReady] = useState(cachedStatus !== undefined);

    useEffect(() => {
        let cancelled = false;

        const load = ({ force = false } = {}) => {
            fetchBillingStatus({ force }).then((next) => {
                if (cancelled) return;
                setStatus(next);
                setReady(true);
            });
        };

        load();

        const onInvalidate = () => load({ force: true });
        window.addEventListener(BILLING_STATUS_INVALIDATE_EVENT, onInvalidate);
        return () => {
            cancelled = true;
            window.removeEventListener(BILLING_STATUS_INVALIDATE_EVENT, onInvalidate);
        };
    }, []);

    return {
        status,
        ready,
        inactive: isBillingInactive(status),
        refresh: async () => {
            const next = await fetchBillingStatus({ force: true });
            setStatus(next);
            setReady(true);
            return next;
        },
    };
}

/** Routes still reachable when trial/subscription is inactive. */
export function isBillingRestrictedPathAllowed(pathname = '') {
    if (pathname === '/password-required') return true;
    if (pathname === '/account-setting') return true;
    if (pathname === '/select-store') return true;
    if (pathname === '/select-plan' || pathname.startsWith('/select-plan/')) return true;
    return false;
}
